import { QueryClient } from '@tanstack/react-query';

import { ApiError } from './http';

/**
 * Settings tuned for mobile use: the network is intermittent, and retrying a
 * request that failed for a business reason (403, 404, 422) only delays showing
 * the error.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      // On mobile the app returns to the foreground constantly: refetching on
      // every focus would produce bursts of pointless requests.
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      retry: (failureCount, error) => {
        if (error instanceof ApiError) {
          // Warning: 429 is the exception: the request is not at fault, it merely
          // arrived too early. Replaying it is exactly right — and it is what
          // stops a legitimate burst (adjusting several filters in a row)
          // leaving an error frozen on screen while all is well a second later.
          if (error.statusCode === 429) return failureCount < 3;

          // Every other 4xx: the request is at fault, replaying changes
          // nothing.
          if (error.statusCode >= 400 && error.statusCode < 500) return false;
        }
        return failureCount < 2;
      },
      // Growing, capped backoff: 1 s, 2 s, 4 s. Replaying a 429 immediately
      // would only burn the quota that was just reached.
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 4000),
    },
    mutations: { retry: false },
  },
});
